import React, { useEffect, useState } from 'react';
import axios from 'axios';

// Auth and Token decoding
import { jwtDecode } from "jwt-decode";
import { isAuthenticated } from "utils/auth";

// reactstrap components
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Table,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Alert,
  Spinner,
  UncontrolledTooltip // Added for button tooltips
} from "reactstrap";

// core components
import IndexNavbar from "components/Navbars/IndexNavbar.js";
import VendorsPageHeader from "components/Headers/VendorsPageHeader";
import IndexFooter from "components/Footers/IndexFooter.js";

import { Link } from "react-router-dom";

// Base URLs for API
const VENDOR_BASE_URL = "https://api.pembrokefarmersmarket.ca/api/vendors"; // Vendor operations
const ADMIN_ACTIONS_BASE_URL = "https://api.pembrokefarmersmarket.ca/api/admin"; // Promote/Demote operations

const VendorManagement = () => {
  const [vendors, setVendors] = useState([]);
  // Added password back to state
  const [formData, setFormData] = useState({
    name: '',
    businessName: '',
    type: '',
    description: '',
    status: 'Active',
    email: '',
    phone: '',
    password: '' // Password for creation
  });
  const [editingId, setEditingId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false); // For main form
  const [isActionLoading, setIsActionLoading] = useState(null); // For promote/demote buttons (track by ID)
  const [feedback, setFeedback] = useState({ message: '', type: '' });
  const [isAdmin, setIsAdmin] = useState(false);

  // --- Check Admin Status (remains the same) ---
  useEffect(() => {
    if (isAuthenticated()) {
      const token = localStorage.getItem("token");
      try {
        const decoded = jwtDecode(token);
        if (decoded.user && decoded.user.role === 'admin') {
          setIsAdmin(true);
        } else {
          setIsAdmin(false);
        }
      } catch (e) {
        console.error("Error decoding token:", e);
        setIsAdmin(false);
      }
    } else {
      setIsAdmin(false);
    }
  }, []);

  // Fetch vendors (remains the same)
  const fetchVendors = async () => {
    setIsLoading(true);
    setFeedback({ message: '', type: '' }); // Clear feedback on fetch
    try {
      const res = await axios.get(VENDOR_BASE_URL);
      setVendors(res.data);
    } catch (error) {
      console.error("Error fetching vendors:", error.message);
      setFeedback({ message: 'Error fetching vendors.', type: 'danger' });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchVendors();
  }, []);

  // Add/remove body class (remains the same)
  useEffect(() => {
    document.body.classList.add("landing-page");
    window.scrollTo(0, 0);
    document.body.scrollTop = 0;
    return function cleanup() {
      document.body.classList.remove("landing-page");
    };
  }, []);

  const handleChange = e => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Updated resetForm to include password
  const resetForm = () => {
    setFormData({
        name: '',
        businessName: '',
        type: '',
        description: '',
        status: 'Active',
        email: '',
        phone: '',
        password: '' // Clear password
    });
    setEditingId(null);
  };

  // Handle Add/Update Vendor Submission (remains largely the same)
  const handleSubmit = async e => {
    e.preventDefault();
    setIsSubmitting(true);
    setFeedback({ message: '', type: '' });
    const token = localStorage.getItem("token");

    if (!isAdmin || !token) {
        setFeedback({ message: 'Unauthorized action.', type: 'danger' });
        setIsSubmitting(false);
        return;
    }
    if (!formData.name || !formData.businessName || !formData.email) {
         setFeedback({ message: 'Owner Name, Business Name, and Email are required.', type: 'warning' });
         setIsSubmitting(false);
         return;
    }
    if (!editingId && !formData.password) { // Check password only on add
        setFeedback({ message: 'Password is required when adding a new vendor.', type: 'warning' });
        setIsSubmitting(false);
        return;
    }

    const url = editingId ? `${VENDOR_BASE_URL}/${editingId}` : VENDOR_BASE_URL;
    const method = editingId ? 'put' : 'post';
    const config = { headers: { 'Authorization': `Bearer ${token}` } };
    const payload = { ...formData };
    if (editingId && !payload.password) { // Don't send empty password on update
        delete payload.password;
    }

    try {
      await axios[method](url, payload, config);
      setFeedback({ message: `Vendor ${editingId ? 'updated' : 'added'} successfully!`, type: 'success' });
      resetForm();
      fetchVendors(); // Refresh list might be needed if status changes affect display
    } catch (error) {
      console.error(`Error ${editingId ? 'updating' : 'adding'} vendor:`, error.response?.data || error.message);
      const errMsg = error.response?.data?.message || `Error ${editingId ? 'updating' : 'adding'} vendor.`;
      setFeedback({ message: errMsg, type: 'danger' });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Updated handleEdit to clear password field (remains the same)
  const handleEdit = vendor => {
     if (!isAdmin) return;
    setFormData({
      name: vendor.name || '',
      businessName: vendor.businessName || '',
      type: vendor.type || '',
      description: vendor.description || '',
      status: vendor.status || '',
      email: vendor.email || '',
      phone: vendor.phone || '',
      password: '' // Clear password field when editing starts
    });
    setEditingId(vendor._id);
    setFeedback({ message: '', type: '' });
    window.scrollTo(0, 0);
  };

  // Handle Delete (remains the same)
  const handleDelete = async id => {
     if (!isAdmin) return;
    if (window.confirm('Are you sure you want to delete this vendor? This action cannot be undone.')) {
      // ... (rest of delete logic remains the same)
       setFeedback({ message: '', type: '' });
      const token = localStorage.getItem("token");
      if (!token) {
          setFeedback({ message: 'Authentication required.', type: 'danger' });
          return;
      }
      const config = { headers: { 'Authorization': `Bearer ${token}` } };

      try {
        await axios.delete(`${VENDOR_BASE_URL}/${id}`, config);
        setFeedback({ message: 'Vendor deleted successfully!', type: 'success' });
        fetchVendors();
        if (editingId === id) {
          resetForm();
        }
      } catch (error) {
        console.error("Delete failed:", error.response?.data || error.message);
        const errMsg = error.response?.data?.message || 'Error deleting vendor.';
        setFeedback({ message: errMsg, type: 'danger' });
      }
    }
  };

  // --- Promote Vendor ---
  const handlePromote = async (vendorId, vendorEmail) => {
    if (!isAdmin) return;
    const initialPassword = window.prompt(`Enter an initial password (min 6 chars) for admin account ${vendorEmail}:`);

    if (!initialPassword || initialPassword.length < 6) {
      alert("Promotion cancelled: Initial password is required and must be at least 6 characters long.");
      return;
    }

    setIsActionLoading(vendorId); // Set loading state for this specific vendor row
    setFeedback({ message: '', type: '' });
    const token = localStorage.getItem("token");
    const config = { headers: { 'Authorization': `Bearer ${token}` } };
    const PROMOTE_API_URL = `${ADMIN_ACTIONS_BASE_URL}/promote/${vendorId}`;

    try {
      const response = await axios.post(PROMOTE_API_URL, { initialPassword }, config);
      setFeedback({ message: response.data.message || `Vendor ${vendorEmail} promoted successfully!`, type: 'success' });
      // Optionally, you might want to refetch vendors if the backend adds an 'isAdmin' flag
      // fetchVendors();
    } catch (error) {
      console.error("Promotion failed:", error.response?.data || error.message);
      const errMsg = error.response?.data?.message || 'Error promoting vendor.';
      setFeedback({ message: errMsg, type: 'danger' });
    } finally {
      setIsActionLoading(null); // Clear loading state
    }
  };

  // --- Demote Admin ---
  const handleDemote = async (vendorId, vendorEmail) => { // Use vendorId for loading state consistency
    if (!isAdmin) return;
    // ** ASSUMPTION: Using vendor's email to identify admin user for deletion **
    // ** Adjust URL if your backend route is different (e.g., uses admin ID) **
    const DEMOTE_API_URL = `${ADMIN_ACTIONS_BASE_URL}/demoteByEmail`; // Example endpoint

    if (window.confirm(`Are you sure you want to remove admin privileges for ${vendorEmail}? The vendor record will remain.`)) {
      setIsActionLoading(vendorId); // Set loading state for this specific vendor row
      setFeedback({ message: '', type: '' });
      const token = localStorage.getItem("token");
      const config = { headers: { 'Authorization': `Bearer ${token}` } };

      try {
        // Sending email in the request body as assumed before
        const response = await axios.post(DEMOTE_API_URL, { email: vendorEmail }, config);
        setFeedback({ message: response.data.message || `Admin ${vendorEmail} demoted successfully!`, type: 'success' });
        // Optionally, refetch vendors if backend modifies vendor status or an 'isAdmin' flag
        // fetchVendors();
      } catch (error) {
        console.error("Demotion failed:", error.response?.data || error.message);
        const errMsg = error.response?.data?.message || 'Error demoting admin.';
        setFeedback({ message: errMsg, type: 'danger' });
      } finally {
        setIsActionLoading(null); // Clear loading state
      }
    }
  };


  // Filter vendors based on admin status (remains the same)
  const displayVendors = isAdmin ? vendors : vendors.filter(v => v.status === 'Active');

  return (
    <>
      <IndexNavbar />
      <VendorsPageHeader />
      <br />
      <div className="main">
        <div className="section">
          <Container>
             <Row>
                <Col md="12">
                  <h2 className="title text-center">{isAdmin ? "Vendor Management" : "Our Vendors"}</h2>
                   {/* Global Feedback Area */}
                    {feedback.message && (
                        <Alert color={feedback.type} isOpen={!!feedback.message} toggle={() => setFeedback({ message: '', type: '' })}>
                        {feedback.message}
                        </Alert>
                    )}
                  <hr/>
                </Col>
             </Row>
            <Row>
              {/* Form Section - Only render if Admin */}
              {isAdmin && (
                <Col md="12">
                  <Card>
                     <CardHeader>
                        <CardTitle tag="h4">{editingId ? 'Edit Vendor' : 'Add New Vendor'}</CardTitle>
                     </CardHeader>
                    <CardBody>
                      {/* Form-specific feedback can go here if needed */}
                      <Form onSubmit={handleSubmit}>
                         <Row form>
                          <Col md={6}>
                            <FormGroup>
                              <Label for="vendorOwnerName">Owner Name</Label>
                              <Input id="vendorOwnerName" name="name" placeholder="Owner's Full Name" value={formData.name} onChange={handleChange} required disabled={isSubmitting}/>
                            </FormGroup>
                          </Col>
                           <Col md={6}>
                            <FormGroup>
                              <Label for="vendorBusinessName">Business Name</Label>
                              <Input id="vendorBusinessName" name="businessName" placeholder="Business Name" value={formData.businessName} onChange={handleChange} required disabled={isSubmitting}/>
                            </FormGroup>
                          </Col>
                        </Row>
                        <Row form>
                          <Col md={6}>
                            <FormGroup>
                              <Label for="vendorType">Type</Label>
                              <Input id="vendorType" name="type" placeholder="e.g., Produce, Crafts" value={formData.type} onChange={handleChange} required disabled={isSubmitting}/>
                            </FormGroup>
                          </Col>
                          <Col md={6}>
                             <FormGroup>
                              <Label for="vendorStatus">Status</Label>
                               <Input type="select" name="status" id="vendorStatus" value={formData.status} onChange={handleChange} required disabled={isSubmitting}>
                                <option value="Active">Active</option>
                                <option value="Pending">Pending</option>
                                <option value="Inactive">Inactive</option>
                              </Input>
                            </FormGroup>
                          </Col>
                        </Row>
                         <Row form>
                          <Col md={6}>
                            <FormGroup>
                              <Label for="vendorEmail">Email</Label>
                              <Input id="vendorEmail" name="email" type="email" placeholder="vendor@example.com" value={formData.email} onChange={handleChange} required disabled={isSubmitting}/>
                            </FormGroup>
                          </Col>
                           <Col md={6}>
                             <FormGroup>
                              <Label for="vendorPhone">Phone</Label>
                              <Input id="vendorPhone" name="phone" placeholder="Contact Number" value={formData.phone} onChange={handleChange} disabled={isSubmitting}/>
                             </FormGroup>
                           </Col>
                        </Row>
                        <Row form>
                            <Col md={6}>
                                <FormGroup>
                                    <Label for="vendorPassword">Password {editingId ? '(Optional: only to change)' : '(Required)'}</Label>
                                    <Input
                                        id="vendorPassword"
                                        name="password"
                                        type="password"
                                        placeholder={editingId ? "Enter new password to change" : "Set Initial Password"}
                                        value={formData.password}
                                        onChange={handleChange}
                                        required={!editingId}
                                        disabled={isSubmitting}
                                    />
                                </FormGroup>
                            </Col>
                        </Row>
                        <Row form>
                            <Col>
                                <FormGroup>
                                    <Label for="vendorDescription">Description</Label>
                                    <Input id="vendorDescription" name="description" type="textarea" placeholder="Brief description..." value={formData.description} onChange={handleChange} rows="3" disabled={isSubmitting} />
                                </FormGroup>
                            </Col>
                        </Row>
                        <Row>
                           <Col className="text-right">
                              {editingId && (
                                <Button className="btn-round mr-1" color="info" outline type="button" onClick={resetForm} disabled={isSubmitting}>
                                  Cancel Edit
                                </Button>
                              )}
                              <Button className="btn-round" color="primary" type="submit" disabled={isSubmitting}>
                                {isSubmitting ? <><Spinner size="sm" /> Processing...</> : (editingId ? 'Update Vendor' : 'Add Vendor')}
                              </Button>
                           </Col>
                        </Row>
                      </Form>
                    </CardBody>
                  </Card>
                </Col>
              )}

              {/* Vendor List Section - Updated Columns and Actions */}
              <Col md="12">
                <Card className="card-plain">
                  <CardHeader>
                    <CardTitle tag="h4">{isAdmin ? "Vendor List" : "Active Vendors"}</CardTitle>
                  </CardHeader>
                  <CardBody>
                    {isLoading ? (
                      <div className="text-center"><Spinner color="primary" /> Loading vendors...</div>
                    ) : displayVendors.length > 0 ? (
                      <Table responsive striped>
                        <thead className="text-primary">
                          <tr>
                            <th>Business Name</th>
                            {isAdmin && <th>Owner Name</th>}
                            <th>Type</th>
                            <th>Description</th>
                            {isAdmin && <th>Status</th>}
                            {isAdmin && <th>Email</th>}
                            {isAdmin && <th>Phone</th>}
                            {isAdmin && <th className="text-right">Actions</th>}
                          </tr>
                        </thead>
                        <tbody>
                          {displayVendors.map(v => (
                            <tr key={v._id}>
                                <td>
                                  {/* Wrap Business Name (or fallback name) in a Link */}
                                  <Link to={`/vendor/${v._id}`}>
                                    {v.businessName || v.name}
                                  </Link>
                                </td>
                              {isAdmin && <td>{v.name}</td>}
                              <td>{v.type}</td>
                              {/* Consider truncating long descriptions or using tooltips */}
                              <td style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v.description}</td>
                              {isAdmin && <td>{v.status}</td>}
                              {isAdmin && <td>{v.email}</td>}
                              {isAdmin && <td>{v.phone}</td>}
                              {isAdmin && (
                                <td className="text-right" style={{ display: 'flex', justifyContent: 'flex-end', gap: '5px', minWidth: '150px' }}> {/* Adjusted minWidth */}
                                  {/* Promote Button */}
                                  <Button className="btn-icon btn-round" color="success" size="sm" id={`promote-${v._id}`} onClick={() => handlePromote(v._id, v.email)} disabled={isActionLoading === v._id} >
                                     {isActionLoading === v._id ? <Spinner size="sm" color="light" /> : <i className="fa fa-user-plus"/>}
                                  </Button>
                                  <UncontrolledTooltip placement="top" target={`promote-${v._id}`} delay={0}>
                                     Promote to Admin
                                  </UncontrolledTooltip>

                                   {/* Demote Button */}
                                   <Button className="btn-icon btn-round" color="warning" size="sm" id={`demote-${v._id}`} onClick={() => handleDemote(v._id, v.email)} disabled={isActionLoading === v._id} >
                                      {isActionLoading === v._id ? <Spinner size="sm" color="light" /> : <i className="fa fa-user-times"/>}
                                  </Button>
                                  <UncontrolledTooltip placement="top" target={`demote-${v._id}`} delay={0}>
                                     Demote Admin
                                  </UncontrolledTooltip>

                                  {/* Edit Button */}
                                  <Button className="btn-icon btn-round" color="info" size="sm" id={`edit-${v._id}`} onClick={() => handleEdit(v)} disabled={isActionLoading === v._id} >
                                    <i className="fa fa-pencil"/>
                                  </Button>
                                   <UncontrolledTooltip placement="top" target={`edit-${v._id}`} delay={0}>
                                     Edit Vendor
                                  </UncontrolledTooltip>

                                  {/* Delete Button */}
                                  <Button className="btn-icon btn-round" color="danger" size="sm" id={`delete-${v._id}`} onClick={() => handleDelete(v._id)} disabled={isActionLoading === v._id} >
                                     <i className="fa fa-trash"/>
                                  </Button>
                                   <UncontrolledTooltip placement="top" target={`delete-${v._id}`} delay={0}>
                                     Delete Vendor
                                  </UncontrolledTooltip>
                                </td>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    ) : (
                       <p className="text-center text-muted">No vendors to display.</p>
                    )}
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </Container>
        </div>
      </div>
      <IndexFooter />
    </>
  );
};

export default VendorManagement;